/**
 * 매핑 설계서 — 테이블·컬럼·코드 매핑을 프로젝트마다 판(버전)으로 저장한다 (backend services/mapping_spec_store).
 *
 * 변환및적재 노드는 설계서의 매핑 하나(매핑ID)를 **판을 정해서** 가져온다 — 노드에는 참조
 * `mappingSpec: {projectId, mappingId, version}` 와 그 판으로 만든 매핑 사본(`mapping`)이 함께 저장되고,
 * 배포는 그 사본으로 한다. 설계서를 고쳐도 노드에서 「반영」을 누르기 전에는 배포본이 바뀌지 않는다.
 *
 * 규칙유형 → 실행 매핑({target, source, expr}):
 *   직접 → source · 식 → expr · 상수 → expr 리터럴 · 기본값 → nvl(소스, 값)
 *   코드 → decode(소스, {원본: 변환}, 그 외) — 코드표가 식에 함께 들어가 실행 때 설계서를 찾지 않는다 · 제외 → 넣지 않음
 */
import { be1Fetch } from '../config/api';
import { errorFromResponse } from './errorText';
import type { MappingItem } from '../components/workflow/panels/PropertyPanel/TransformMappingModal';

export type RuleType = 'DIRECT' | 'EXPR' | 'CONST' | 'DEFAULT' | 'CODE' | 'SKIP';
export const RULES: RuleType[] = ['DIRECT', 'EXPR', 'CONST', 'DEFAULT', 'CODE', 'SKIP'];
export const MODES = ['WRITE', 'APPEND', 'UPSERT'] as const;

export interface SpecTable { mappingId: string; srcConn: string; srcTable: string; where: string; tgtConn: string; tgtTable: string; mode: string; order: string; note: string }
export interface SpecColumn { mappingId: string; target: string; source: string; rule: string; value: string; note: string }
export interface SpecCode { group: string; from: string; to: string; note: string }
export interface MappingSpec {
  projectId: string; version: number; savedAt: string | null; savedBy: string;
  tables: SpecTable[]; columns: SpecColumn[]; codes: SpecCode[];
}
export type Sheet = 'tables' | 'columns' | 'codes';
export interface SpecRef { projectId: string; mappingId: string; version: number }

export const emptyTable = (): SpecTable => ({ mappingId: '', srcConn: '', srcTable: '', where: '', tgtConn: '', tgtTable: '', mode: 'WRITE', order: '', note: '' });
export const emptyColumn = (mappingId = ''): SpecColumn => ({ mappingId, target: '', source: '', rule: 'DIRECT', value: '', note: '' });
export const emptyCode = (group = ''): SpecCode => ({ group, from: '', to: '', note: '' });

// ---------------------------------------------------------------- API

const base = (projectId: string) => `/api/mapping-specs/${encodeURIComponent(projectId)}`;

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

export async function loadSpec(projectId: string, version?: number): Promise<MappingSpec> {
  return json(await be1Fetch(`${base(projectId)}${version ? `?version=${version}` : ''}`, { credentials: 'include' }));
}

/** 노드 속성이 「새 판 있음」을 볼 때 — 노드를 옮겨 다닐 때마다 다시 읽지 않게 잠깐 들고 있는다 */
const latestCache = new Map<string, { at: number; spec: Promise<MappingSpec> }>();
export function loadLatestCached(projectId: string, maxAgeMs = 20000): Promise<MappingSpec> {
  const hit = latestCache.get(projectId);
  if (hit && Date.now() - hit.at < maxAgeMs) return hit.spec;
  const spec = loadSpec(projectId);
  latestCache.set(projectId, { at: Date.now(), spec });
  spec.catch(() => latestCache.delete(projectId));
  return spec;
}

export async function saveSpec(projectId: string, spec: Pick<MappingSpec, 'tables' | 'columns' | 'codes'>, baseVersion: number): Promise<MappingSpec> {
  const saved = await json<MappingSpec>(await be1Fetch(base(projectId), {
    method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ baseVersion, tables: spec.tables, columns: spec.columns, codes: spec.codes }),
  }));
  latestCache.set(projectId, { at: Date.now(), spec: Promise.resolve(saved) });
  return saved;
}

export interface SpecHistoryRow { version: number; savedAt: string | null; savedBy: string; tables: number; columns: number; codes: number }
export async function loadHistory(projectId: string): Promise<SpecHistoryRow[]> {
  return (await json<{ history: SpecHistoryRow[] }>(await be1Fetch(`${base(projectId)}/history`, { credentials: 'include' }))).history;
}

export interface ImportResult {
  sheets: Array<{ sheet: Sheet; rows: any[]; unknownHeaders: string[]; name: string }>;
  errors: Array<{ name: string; code: string; params: Record<string, unknown> }>;
}
export async function importCsv(projectId: string, files: File[]): Promise<ImportResult> {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return json(await be1Fetch(`${base(projectId)}/import`, { method: 'POST', credentials: 'include', body: fd }));
}

/** 변환식이 실행 때 받아들여지는지 — 식 → 오류 글(문제 없으면 빈 글) */
export async function checkExprs(exprs: string[]): Promise<Record<string, string>> {
  const uniq = Array.from(new Set(exprs.filter(Boolean)));
  if (!uniq.length) return {};
  const r = await json<{ results: Array<{ expr: string; error: string }> }>(await be1Fetch('/api/mapping-specs/check-exprs', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ exprs: uniq }),
  }));
  return Object.fromEntries(r.results.map((x) => [x.expr, x.error]));
}

function download(blob: Blob, name: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export async function downloadTemplate(sheet: Sheet, lang: string) {
  const r = await be1Fetch(`/api/mapping-specs/template/${sheet}?lang=${lang === 'en' ? 'en' : 'ko'}`, { credentials: 'include' });
  if (!r.ok) throw await errorFromResponse(r);
  download(await r.blob(), `mapping_${sheet}.csv`);
}

export async function exportSheet(sheet: Sheet, rows: any[], lang: string, prefix = 'mapping') {
  const r = await be1Fetch(`/api/mapping-specs/export/${sheet}?lang=${lang === 'en' ? 'en' : 'ko'}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rows }),
  });
  if (!r.ok) throw await errorFromResponse(r);
  download(await r.blob(), `${prefix}_${sheet}.csv`);
}

// ---------------------------------------------------------------- 규칙 → 실행 매핑

const PY_KEYWORDS = new Set(['and', 'as', 'assert', 'async', 'await', 'break', 'class', 'continue', 'def', 'del', 'elif', 'else', 'except',
  'finally', 'for', 'from', 'global', 'if', 'import', 'in', 'is', 'lambda', 'nonlocal', 'not', 'or', 'pass', 'raise', 'return',
  'try', 'while', 'with', 'yield', 'row', 'None', 'True', 'False']);

/** 파이썬 글 리터럴 — 작은따옴표로, \ 와 ' 를 이스케이프 */
export const pyStr = (v: string) => `'${v.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '\\r')}'`;

/** 식 안에서 소스 항목을 가리키는 법 — 이름으로 쓸 수 있으면 이름, 아니면 row['이름'] */
export function srcRef(name: string): string {
  return /^[A-Za-z][A-Za-z0-9_]*$/.test(name) && !PY_KEYWORDS.has(name) ? name : `row[${pyStr(name)}]`;
}

/**
 * 상수·기본값 칸의 글 → 식 리터럴. 따옴표로 감싸 있으면 그대로, NULL 은 None,
 * 0 으로 시작하지 않는 숫자는 숫자로(앞자리 0 이 있는 코드·번호는 글로 둔다), 나머지는 글로.
 */
export function literal(v: string): string {
  const s = v.trim();
  if (/^'.*'$/s.test(s) || /^".*"$/s.test(s)) return s;
  if (/^(null|none)$/i.test(s)) return 'None';
  if (/^-?(0|[1-9]\d*)(\.\d+)?$/.test(s)) return s;
  return pyStr(s);
}

export interface RuleProblem { target: string; code: 'noSource' | 'noValue' | 'noCodeGroup' | 'badRule' }

/** 컬럼 규칙 하나 → 실행 매핑 하나 (제외는 null). 만들 수 없으면 problem */
export function ruleToItem(col: SpecColumn, codes: SpecCode[]): { item: MappingItem | null; problem?: RuleProblem } {
  const target = col.target.trim();
  const source = col.source.trim();
  const value = col.value ?? '';
  const rule = (col.rule || 'DIRECT').toUpperCase() as RuleType;
  const fail = (code: RuleProblem['code']) => ({ item: null, problem: { target, code } });
  switch (rule) {
    case 'SKIP': return { item: null };
    case 'DIRECT': return source ? { item: { target, source, expr: '' } } : fail('noSource');
    case 'EXPR': return value.trim() ? { item: { target, source, expr: value.trim() } } : fail('noValue');
    case 'CONST': return value.trim() ? { item: { target, source: '', expr: literal(value) } } : fail('noValue');
    case 'DEFAULT':
      if (!source) return fail('noSource');
      return value.trim() ? { item: { target, source, expr: `nvl(${srcRef(source)}, ${literal(value)})` } } : fail('noValue');
    case 'CODE': {
      if (!source) return fail('noSource');
      const group = value.trim();
      const rows = codes.filter((c) => c.group.trim() === group);
      if (!group || !rows.length) return fail('noCodeGroup');
      const other = rows.find((c) => c.from.trim() === '*');
      const table = rows.filter((c) => c.from.trim() !== '*').map((c) => `${pyStr(c.from.trim())}: ${literal(c.to)}`).join(', ');
      return { item: { target, source, expr: `decode(${srcRef(source)}, {${table}}${other ? `, ${literal(other.to)}` : ''})` } };
    }
    default: return fail('badRule');
  }
}

/** 설계서의 매핑 하나 → 노드에 넣을 매핑 사본 */
export function specToMapping(spec: Pick<MappingSpec, 'columns' | 'codes'>, mappingId: string): { items: MappingItem[]; problems: RuleProblem[] } {
  const items: MappingItem[] = [];
  const problems: RuleProblem[] = [];
  spec.columns.filter((c) => c.mappingId.trim() === mappingId.trim() && c.target.trim()).forEach((c) => {
    const r = ruleToItem(c, spec.codes);
    if (r.item) items.push(r.item);
    if (r.problem) problems.push(r.problem);
  });
  return { items, problems };
}

/** 두 매핑 사본의 다른 타깃 컬럼 수 (더해짐·빠짐·바뀜) */
export function changedTargets(a: MappingItem[], b: MappingItem[]): number {
  const key = (m: MappingItem) => `${m.source}\u0000${m.expr}`;
  const ma = new Map(a.map((m) => [m.target, key(m)]));
  const mb = new Map(b.map((m) => [m.target, key(m)]));
  let n = 0;
  ma.forEach((v, k) => { if (mb.get(k) !== v) n += 1; });
  mb.forEach((_, k) => { if (!ma.has(k)) n += 1; });
  return n;
}

/** 'SCHEMA.TABLE' → {schema, table} */
export function splitTable(name: string): { schema: string; table: string } {
  const s = name.trim();
  const i = s.lastIndexOf('.');
  return i > 0 ? { schema: s.slice(0, i), table: s.slice(i + 1) } : { schema: '', table: s };
}

/** 노드의 타깃 테이블과 설계서 타깃 테이블이 같은가 — 스키마를 안 적은 쪽은 테이블 이름만 본다 */
export function sameTable(specTable: string, schema: string, table: string): boolean {
  const a = splitTable(specTable);
  if (a.table.toUpperCase() !== (table || '').toUpperCase()) return false;
  return !a.schema || !schema || a.schema.toUpperCase() === schema.toUpperCase();
}

// ---------------------------------------------------------------- 검증

export interface DictColumn { name: string; dataType?: string; nullable?: boolean; length?: number; lengthUnit?: string }
export interface SpecIssue {
  sheet: Sheet; row: number; level: 'error' | 'warn';
  /** mapping:issue.<code> */
  code: string;
  params?: Record<string, string | number>;
}

/** 시트 안에서만 볼 수 있는 문제 — DB 사전 없이 */
export function checkSpec(spec: Pick<MappingSpec, 'tables' | 'columns' | 'codes'>): SpecIssue[] {
  const out: SpecIssue[] = [];
  const ids = new Map<string, number>();
  spec.tables.forEach((t, row) => {
    const id = t.mappingId.trim();
    if (!id) out.push({ sheet: 'tables', row, level: 'error', code: 'noMappingId' });
    else if (ids.has(id)) out.push({ sheet: 'tables', row, level: 'error', code: 'dupMappingId', params: { id } });
    else ids.set(id, row);
    if (!t.tgtTable.trim()) out.push({ sheet: 'tables', row, level: 'error', code: 'noTgtTable' });
    if (!t.srcTable.trim()) out.push({ sheet: 'tables', row, level: 'warn', code: 'noSrcTable' });
    if (!(MODES as readonly string[]).includes((t.mode || '').toUpperCase())) out.push({ sheet: 'tables', row, level: 'error', code: 'badMode', params: { mode: t.mode } });
    if (t.order.trim() && !/^\d+$/.test(t.order.trim())) out.push({ sheet: 'tables', row, level: 'warn', code: 'badOrder', params: { order: t.order } });
  });
  const groups = new Set(spec.codes.map((c) => c.group.trim()).filter(Boolean));
  const targets = new Set<string>();
  spec.columns.forEach((c, row) => {
    const id = c.mappingId.trim();
    if (!ids.has(id)) out.push({ sheet: 'columns', row, level: 'error', code: 'unknownMappingId', params: { id } });
    if (!c.target.trim()) { out.push({ sheet: 'columns', row, level: 'error', code: 'noTarget' }); return; }
    const k = `${id}\u0000${c.target.trim().toUpperCase()}`;
    if (targets.has(k)) out.push({ sheet: 'columns', row, level: 'error', code: 'dupTarget', params: { col: c.target } });
    targets.add(k);
    if (!RULES.includes((c.rule || '').toUpperCase() as RuleType)) { out.push({ sheet: 'columns', row, level: 'error', code: 'badRule', params: { rule: c.rule } }); return; }
    const r = ruleToItem(c, spec.codes);
    if (r.problem) {
      out.push({ sheet: 'columns', row, level: 'error', code: r.problem.code, params: { col: c.target, group: c.value } });
    } else if (c.rule.toUpperCase() === 'CODE' && groups.has(c.value.trim())
      && !spec.codes.some((x) => x.group.trim() === c.value.trim() && x.from.trim() === '*')) {
      out.push({ sheet: 'columns', row, level: 'warn', code: 'codeNoDefault', params: { group: c.value } });
    }
  });
  const pairs = new Set<string>();
  spec.codes.forEach((c, row) => {
    if (!c.group.trim()) out.push({ sheet: 'codes', row, level: 'error', code: 'noGroup' });
    const k = `${c.group.trim()}\u0000${c.from.trim()}`;
    if (pairs.has(k)) out.push({ sheet: 'codes', row, level: 'error', code: 'dupCode', params: { group: c.group, from: c.from } });
    pairs.add(k);
  });
  return out;
}

const CHAR_TYPES = /CHAR|TEXT|CLOB|STRING/i;

/**
 * DB 사전과 견준다. dict: '연결\u0000테이블' → 컬럼 목록(못 읽었으면 null — 그 테이블은 건너뛰고 알린다).
 *   · 타깃·소스 컬럼이 테이블에 있는가
 *   · 타깃의 NOT NULL 컬럼이 모두 매핑됐는가(제외가 아니고)
 *   · 소스 글 길이가 타깃보다 긴가(잘리거나 실패)
 */
export function checkAgainstDb(spec: Pick<MappingSpec, 'tables' | 'columns'>, dict: Map<string, DictColumn[] | null>): SpecIssue[] {
  const out: SpecIssue[] = [];
  const key = (conn: string, table: string) => `${conn.trim()}\u0000${table.trim().toUpperCase()}`;
  const find = (cols: DictColumn[] | null | undefined, name: string) => cols?.find((c) => c.name.toUpperCase() === name.trim().toUpperCase());
  spec.tables.forEach((t, row) => {
    const tgt = dict.get(key(t.tgtConn, t.tgtTable));
    const src = t.srcTable.trim() ? dict.get(key(t.srcConn, t.srcTable)) : undefined;
    if (t.tgtTable.trim() && tgt === null) out.push({ sheet: 'tables', row, level: 'warn', code: 'tgtUnreadable', params: { table: t.tgtTable } });
    if (t.tgtTable.trim() && tgt && !tgt.length) out.push({ sheet: 'tables', row, level: 'error', code: 'tgtNotFound', params: { table: t.tgtTable } });
    if (t.srcTable.trim() && src === null) out.push({ sheet: 'tables', row, level: 'warn', code: 'srcUnreadable', params: { table: t.srcTable } });
    if (t.srcTable.trim() && src && !src.length) out.push({ sheet: 'tables', row, level: 'error', code: 'srcNotFound', params: { table: t.srcTable } });
    const id = t.mappingId.trim();
    const mine = spec.columns.map((c, i) => ({ c, i })).filter((x) => x.c.mappingId.trim() === id);
    mine.forEach(({ c, i }) => {
      const tc = tgt?.length ? find(tgt, c.target) : undefined;
      if (tgt?.length && c.target.trim() && !tc) out.push({ sheet: 'columns', row: i, level: 'error', code: 'tgtColNotFound', params: { col: c.target, table: t.tgtTable } });
      const rule = (c.rule || '').toUpperCase();
      const usesSource = ['DIRECT', 'DEFAULT', 'CODE'].includes(rule) || (rule === 'EXPR' && !!c.source.trim());
      const sc = src?.length && c.source.trim() ? find(src, c.source) : undefined;
      if (usesSource && src?.length && c.source.trim() && !sc) out.push({ sheet: 'columns', row: i, level: 'error', code: 'srcColNotFound', params: { col: c.source, table: t.srcTable } });
      if (rule === 'DIRECT' && tc && sc && tc.length && sc.length && sc.length > tc.length
        && CHAR_TYPES.test(tc.dataType || '') && CHAR_TYPES.test(sc.dataType || '')) {
        out.push({ sheet: 'columns', row: i, level: 'warn', code: 'lengthShrink', params: { col: c.target, src: sc.length, tgt: tc.length } });
      }
      if (rule === 'DIRECT' && tc && sc && CHAR_TYPES.test(sc.dataType || '') && /NUMBER|NUMERIC|INT|DECIMAL/i.test(tc.dataType || '')) {
        out.push({ sheet: 'columns', row: i, level: 'warn', code: 'charToNumber', params: { col: c.target } });
      }
    });
    if (tgt?.length) {
      const mapped = new Set(mine.filter((x) => (x.c.rule || '').toUpperCase() !== 'SKIP').map((x) => x.c.target.trim().toUpperCase()));
      const missing = tgt.filter((c) => c.nullable === false && !mapped.has(c.name.toUpperCase())).map((c) => c.name);
      if (missing.length) out.push({ sheet: 'tables', row, level: 'error', code: 'notNullMissing', params: { cols: missing.join(', ') } });
    }
  });
  return out;
}
